import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { aiAgentService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Play, Save, Lightbulb, Clock, Brain, CalendarClock,
} from "lucide-react";
import {
  AgentIcon, RiskBadge, ConfidenceBadge, useTenantScope, TenantPicker, SelectTenantNotice,
  PageHeader, fmtDate,
} from "./shared";

const FREQS = ["hourly", "daily", "weekly"];

export default function AgentDetail() {
  const { code } = useParams();
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [data, setData] = useState(null);
  const [memory, setMemory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState({ enabled: true, confidenceThreshold: 0.5, schedEnabled: false, frequency: "daily" });

  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    Promise.all([aiAgentService.get(code, params), aiAgentService.memory(code, params)])
      .then(([d, m]) => {
        setData(d);
        setMemory(m.items || []);
        const a = d.agent;
        setCfg({
          enabled: a.enabled, confidenceThreshold: a.confidenceThreshold ?? 0.5,
          schedEnabled: a.triggers?.schedule?.enabled || false,
          frequency: a.triggers?.schedule?.frequency || "daily",
        });
      })
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, code, params]);

  useEffect(() => { load(); }, [load]);

  const agent = data?.agent;
  const placeholder = agent?.isPlaceholder;

  const run = async () => {
    setRunning(true);
    try { const r = await aiAgentService.run(code, { tenantId: tenantId || undefined }); toast.success(r.summary); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await aiAgentService.configure(code, {
        enabled: cfg.enabled, confidenceThreshold: Number(cfg.confidenceThreshold),
        schedule: { enabled: cfg.schedEnabled, frequency: cfg.frequency },
      }, params);
      toast.success("Agent configuration saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="agent-detail-page">
      <PageHeader icon={(p) => <AgentIcon code={code} {...p} />} title={agent?.name || `${code} Agent`}
        subtitle={agent?.description || "Agent configuration & activity"}>
        <TenantPicker {...scope} />
      </PageHeader>

      <Link to="/admin/agents" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="agent-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Agent Center
      </Link>

      {blocked ? <SelectTenantNotice /> : loading && !data ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : agent ? (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Config */}
          <section className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid="agent-config">
            <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Configuration</h2>
            {placeholder ? (
              <p className="text-xs text-muted-foreground">This agent is a placeholder and can’t be configured yet.</p>
            ) : (
              <>
                <label className="flex items-center justify-between gap-2 text-sm text-foreground">
                  <span>Enabled</span>
                  <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="h-4 w-4 accent-primary" data-testid="config-enabled" />
                </label>
                <div>
                  <label className="text-sm text-foreground flex items-center justify-between">
                    <span>Confidence threshold</span>
                    <span className="text-xs font-semibold text-primary">{Math.round(cfg.confidenceThreshold * 100)}%</span>
                  </label>
                  <input type="range" min="0" max="1" step="0.05" value={cfg.confidenceThreshold}
                    onChange={(e) => setCfg({ ...cfg, confidenceThreshold: e.target.value })}
                    className="w-full accent-primary mt-1" data-testid="config-threshold" />
                </div>
                <div className="border-t border-border pt-3 space-y-2">
                  <label className="flex items-center justify-between gap-2 text-sm text-foreground">
                    <span>Scheduled runs (recommend-only)</span>
                    <input type="checkbox" checked={cfg.schedEnabled} onChange={(e) => setCfg({ ...cfg, schedEnabled: e.target.checked })} className="h-4 w-4 accent-primary" data-testid="config-schedule-enabled" />
                  </label>
                  {cfg.schedEnabled && (
                    <select value={cfg.frequency} onChange={(e) => setCfg({ ...cfg, frequency: e.target.value })}
                      className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground capitalize" data-testid="config-frequency">
                      {FREQS.map((f) => <option key={f} value={f} className="capitalize">{f}</option>)}
                    </select>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={save} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="config-save">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                  </button>
                  <button onClick={run} disabled={running} className="inline-flex items-center justify-center gap-1.5 border border-border rounded-lg px-3 py-2 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-50" data-testid="config-run">
                    {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run now
                  </button>
                </div>
              </>
            )}
            <div className="grid grid-cols-3 gap-2 text-center border-t border-border pt-3">
              {[["Runs", agent.stats?.runs], ["Accepted", agent.stats?.accepted], ["Executed", agent.stats?.actionsExecuted]].map(([l, v]) => (
                <div key={l} className="bg-muted/40 rounded-lg py-1.5"><p className="text-sm font-bold text-foreground">{v || 0}</p><p className="text-[10px] text-muted-foreground">{l}</p></div>
              ))}
            </div>
          </section>

          {/* Recent recommendations + runs */}
          <section className="lg:col-span-2 space-y-4">
            <div className="bg-card border border-border rounded-xl p-5" data-testid="agent-recent-recs">
              <h2 className="text-sm font-bold text-foreground flex items-center gap-2 mb-3"><Lightbulb className="h-4 w-4 text-primary" /> Recent recommendations</h2>
              {(data.recentRecommendations || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">None yet. Run the agent to generate recommendations.</p>
              ) : (
                <div className="space-y-2">
                  {data.recentRecommendations.map((r) => (
                    <div key={r.id} className="border border-border rounded-lg p-3" data-testid={`detail-rec-${r.id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <p className="text-sm text-foreground font-medium">{r.title}</p>
                        <div className="flex items-center gap-1.5"><ConfidenceBadge label={r.confidenceLabel} value={r.confidence} /><RiskBadge level={r.riskLevel} /></div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{r.reason}</p>
                      <p className="text-[11px] text-muted-foreground/70 mt-1 capitalize">{r.status} · {fmtDate(r.createdOn)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-xl p-5" data-testid="agent-recent-runs">
                <h2 className="text-sm font-bold text-foreground flex items-center gap-2 mb-3"><Clock className="h-4 w-4 text-primary" /> Recent runs</h2>
                {(data.recentRuns || []).length === 0 ? <p className="text-xs text-muted-foreground">No runs yet.</p> : (
                  <ul className="space-y-2">
                    {data.recentRuns.map((r) => (
                      <li key={r.id} className="text-xs text-muted-foreground border-b border-border pb-2 last:border-0">
                        <span className="text-foreground capitalize">{r.trigger}</span> · {r.recommendationsGenerated} recs · {fmtDate(r.startedAt)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-card border border-border rounded-xl p-5" data-testid="agent-memory">
                <h2 className="text-sm font-bold text-foreground flex items-center gap-2 mb-3"><Brain className="h-4 w-4 text-primary" /> Agent memory</h2>
                {memory.length === 0 ? <p className="text-xs text-muted-foreground">No observations yet.</p> : (
                  <ul className="space-y-2">
                    {memory.slice(0, 6).map((m) => (
                      <li key={m.id} className="text-xs text-muted-foreground border-b border-border pb-2 last:border-0">
                        <span className="text-foreground capitalize">{(m.key || "").replace("_", " ")}</span> — {m.observation}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
